import React, { type ReactNode, useContext, useEffect, useMemo, useState } from 'react';

export type SwitcherExperimentCohort =
	| 'control'
	| 'variant_1'
	| 'variant_2'
	| 'variant_3'
	| 'not-enrolled'
	| undefined;

const overflowStorageKey = '@atlassian/switcher-overflow-experiment-cohort';

interface useSwitcherThirdPartyExperimentResult {
	cohort: SwitcherExperimentCohort;
	setThirdPartyCohort: (cohort: SwitcherExperimentCohort) => void;
}

type SwitcherExperimentContextProvider = {
	setSwitcherThirdPartyExperimentCohort: (cohort: SwitcherExperimentCohort) => void;
	switcherThirdPartyExperimentCohort: SwitcherExperimentCohort;
};

export const ExperimentContext = React.createContext<SwitcherExperimentContextProvider | undefined>(
	undefined,
);

export const SwitcherExperimentProvider = ({
	children,
}: {
	children: ReactNode;
}): React.JSX.Element => {
	const [switcherThirdPartyExperimentCohort, setSwitcherThirdPartyExperimentCohort] =
		useState<SwitcherExperimentCohort>();

	const contextValue = useMemo<SwitcherExperimentContextProvider>(
		() => ({
			switcherThirdPartyExperimentCohort,
			setSwitcherThirdPartyExperimentCohort,
		}),
		[switcherThirdPartyExperimentCohort, setSwitcherThirdPartyExperimentCohort],
	);

	useEffect(() => {
		if (window?.sessionStorage && switcherThirdPartyExperimentCohort === 'variant_3') {
			window.sessionStorage.setItem(overflowStorageKey, switcherThirdPartyExperimentCohort);
		}
	}, [switcherThirdPartyExperimentCohort]);

	return <ExperimentContext.Provider value={contextValue}>{children}</ExperimentContext.Provider>;
};

export const useExperimentProvider = () => useContext(ExperimentContext);

export const useSwitcherThirdPartyExperiment = (): useSwitcherThirdPartyExperimentResult => {
	const experimentContext = useExperimentProvider();

	return {
		cohort: experimentContext?.switcherThirdPartyExperimentCohort,
		setThirdPartyCohort: experimentContext
			? experimentContext.setSwitcherThirdPartyExperimentCohort
			: () => {},
	};
};

export const useIsSwitcherThirdPartyTestCohort = () => {
	const { cohort } = useSwitcherThirdPartyExperiment();
	if (cohort === 'not-enrolled' || cohort === 'control' || cohort === undefined) {
		return undefined;
	}
	return cohort;
};

export const useIsSwitcherThirdPartyVariantCohort = () => {
	const cohort = useIsSwitcherThirdPartyTestCohort();
	return cohort === 'variant_1' || cohort === 'variant_2' || cohort === 'variant_3';
};

export const useIsSwitcherOverflowTestCohort = () => {
	const cohort = useIsSwitcherThirdPartyTestCohort();
	if (cohort) {
		return cohort === 'variant_3';
	}

	// fallback to session storage
	return window?.sessionStorage?.getItem(overflowStorageKey) === 'variant_3';
};

export const useIsSwitcherDraggableTestCohort = () => {
	const cohort = useIsSwitcherThirdPartyTestCohort();
	return cohort === 'variant_2' || cohort === 'variant_3';
};
